import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import Board from './components/Board';
import { Player, GameBoard, WinningLine } from './types';

const WINNING_COMBINATIONS = [
  [0, 1, 2], // Rows
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6], // Columns
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8], // Diagonals
  [2, 4, 6],
];

function App() {
  const [board, setBoard] = useState<GameBoard>(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState<Player>('X');
  const [winningLine, setWinningLine] = useState<WinningLine>(null);
  const [gameOver, setGameOver] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const checkWinner = useCallback((squares: GameBoard): [Player | null, number[] | null] => {
    for (const combination of WINNING_COMBINATIONS) {
      const [a, b, c] = combination;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return [squares[a] as Player, combination];
      }
    }
    return [null, null];
  }, []);

  const handleClick = (index: number) => {
    if (board[index] || gameOver) return;

    const newBoard = [...board];
    newBoard[index] = currentPlayer;
    setBoard(newBoard);

    const [winner, winningCombination] = checkWinner(newBoard);
    if (winner) {
      setWinningLine(winningCombination);
      setGameOver(true);
    } else if (newBoard.every(cell => cell !== null)) {
      setGameOver(true);
    } else {
      setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X');
    }
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setCurrentPlayer('X');
    setWinningLine(null);
    setGameOver(false);
  };

  const getGameStatus = () => {
    if (winningLine) {
      return `Player ${currentPlayer} Wins!`;
    } else if (gameOver) {
      return 'Draw!';
    } else {
      return `Player ${currentPlayer}'s Turn`;
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className="App">
      <button 
        className="theme-toggle" 
        onClick={toggleTheme} 
        aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
      >
        {isDarkMode ? '☀️' : '🌙'}
      </button>
      <a
        href="https://github.com/abdulhanan1000"
        target="_blank"
        rel="noopener noreferrer"
        className="github-link"
      >
        <i className="fab fa-github"></i>
      </a>
      <h1>Tic Tac Toe</h1>
      <div className="game-status">{getGameStatus()}</div>
      <Board squares={board} winningLine={winningLine} onClick={handleClick} />
      <button className="reset-button" onClick={resetGame}>
        New Game
      </button>
    </div>
  );
}

export default App;
